import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Alert, CircularProgress
} from '@mui/material';
import {
  TrendingUp, People, School, Business, BarChart
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import api from '../../services/api';
import './ReportsDashboard.css';

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

export default function ReportsDashboard() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    overview: {},
    companies: [],
    students: [],
    courses: [],
    progressData: {}
  });

  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [overviewRes, companiesRes, studentsRes, coursesRes, progressRes] = await Promise.all([
          api.get('/reports/overview'),
          api.get('/reports/companies-performance'),
          api.get('/reports/students-overview'),
          api.get('/reports/courses-performance'),
          api.get('/reports/progress-data')
        ]);

        setDashboardData({
          overview: overviewRes.data,
          companies: companiesRes.data,
          students: studentsRes.data,
          courses: coursesRes.data,
          progressData: progressRes.data
        });
      } catch (err) {
        console.error('Erro ao carregar dados do dashboard:', err);
        setError('Não foi possível carregar os dados do dashboard. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const handleTabChange = (tabIndex) => {
    setSelectedTab(tabIndex);
  };

  const StatCard = ({ title, value, subtitle, icon, color = '#3b82f6', trend }) => (
    <Box className="stat-card">
      <Box className="stat-card-header">
        <Box className="stat-card-icon" sx={{ backgroundColor: `${color}20` }}>
          {icon}
        </Box>
        {trend && (
          <Box className={`stat-card-trend ${trend.includes('-') ? 'negative' : ''}`}>
            {trend}
          </Box>
        )}
      </Box>
      <Typography className="stat-card-value">{value}</Typography>
      <Typography className="stat-card-label">{title}</Typography>
      {subtitle && (
        <Typography className="stat-card-subtitle">{subtitle}</Typography>
      )}
    </Box>
  );

  const ProgressBar = ({ value, label }) => (
      <Box className="progress-label-row">
        <Typography className="progress-label">{label}</Typography>
        <Box className="progress-percentage">{value}%</Box>
      </Box>
  );

  if (loading) {
    return (
      <Box className="loading-container">
        <CircularProgress size={60} className="loading-spinner" />
        <Typography className="loading-text">Carregando dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box className="reports-dashboard">
      {/* Header */}
      <Box className="reports-dashboard-header">
        <Typography variant="h1" component="h1">
          Relatórios
        </Typography>
        <Typography variant="body1">
          Insights em tempo real sobre desempenho e progresso
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
          {error}
        </Alert>
      )}

      {/* Stat Cards */}
      <Box className="stat-card-container">
        <StatCard
          title="Total de Estudantes"
          value={dashboardData.overview.totalStudents?.toLocaleString() || '0'}
          subtitle="Usuários ativos"
          icon={<People sx={{ fontSize: '1.5rem' }} />}
          color="#3b82f6"
          trend="+12.5%"
        />
        <StatCard
          title="Cursos Disponíveis"
          value={dashboardData.overview.totalCourses || '0'}
          subtitle="Conteúdos ativos"
          icon={<School sx={{ fontSize: '1.5rem' }} />}
          color="#10b981"
          trend="+3"
        />
        <StatCard
          title="Empresas Parceiras"
          value={dashboardData.overview.totalCompanies || '0'}
          subtitle="Clientes ativos"
          icon={<Business sx={{ fontSize: '1.5rem' }} />}
          color="#f59e0b"
          trend="+2"
        />
        <StatCard
          title="Taxa de Conclusão"
          value={`${dashboardData.overview.completionRate?.toFixed(1) || '0'}%`}
          subtitle="Média geral"
          icon={<TrendingUp sx={{ fontSize: '1.5rem' }} />}
          color="#8b5cf6"
          trend="+5.2%"
        />
      </Box>

      {/* Tabs */}
      <Box className="reports-tabs-container">
        <ul className="reports-tabs-list">
          {[
            { id: 0, label: 'Visão Geral', icon: <BarChart sx={{ fontSize: '1.2rem' }} /> },
            { id: 1, label: 'Empresas', icon: <Business sx={{ fontSize: '1.2rem' }} /> },
            { id: 2, label: 'Estudantes', icon: <People sx={{ fontSize: '1.2rem' }} /> },
            { id: 3, label: 'Cursos', icon: <School sx={{ fontSize: '1.2rem' }} /> }
          ].map((tab) => (
            <li key={tab.id}>
              <button
                className={`reports-tab-button ${selectedTab === tab.id ? 'active' : ''}`}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </Box>

      {/* Tab Content */}
      <Box className="reports-content-section">
        {selectedTab === 0 && (
          <Box className="layout-two-column">
            {/* Progress Chart */}
            <Box className="chart-container">
              <Box className="chart-header">
                <Typography className="chart-title">Progresso Mensal</Typography>
              </Box>
              <Box className="chart-content">
                {dashboardData.progressData?.monthlyProgress ? (
                  <Line
                    data={dashboardData.progressData.monthlyProgress}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: true }
                      },
                      scales: {
                        y: { beginAtZero: true }
                      }
                    }}
                  />
                ) : (
                  <Box className="chart-placeholder">
                    <Typography>Dados não disponíveis</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Status Distribution */}
            <Box className="chart-container">
              <Box className="chart-header">
                <Typography className="chart-title">Status dos Cursos</Typography>
              </Box>
              <Box className="chart-content">
                {dashboardData.progressData?.statusDistribution ? (
                  <Doughnut
                    data={dashboardData.progressData.statusDistribution}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { position: 'bottom' }
                      }
                    }}
                  />
                ) : (
                  <Box className="chart-placeholder">
                    <Typography>Dados não disponíveis</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {selectedTab === 1 && (
          <Box>
            <Box className="section-header">
              <Typography className="section-title">Performance por Empresa</Typography>
            </Box>
            {dashboardData.companies && dashboardData.companies.length > 0 ? (
              <Box className="cards-grid">
                {dashboardData.companies.map((company) => (
                  <Box key={company.id} className="data-card">
                    <Typography className="data-card-title">{company.name}</Typography>
                    <Typography className="data-card-meta">
                      {company.studentCount} estudantes
                    </Typography>
                    <Box className="data-card-content">
                      <ProgressBar value={company.averageProgress} label="Progresso Médio" />
                      <Box className="badge-group">
                        <Box className="badge badge-success">
                          {company.completionRate}% conclusão
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box className="empty-state">
                <Typography className="empty-state-title">Nenhuma empresa encontrada</Typography>
                <Typography className="empty-state-text">
                  Não há dados de desempenho de empresas para exibir no momento.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {selectedTab === 2 && (
          <Box>
            <Box className="section-header">
              <Typography className="section-title">Top Estudantes</Typography>
            </Box>
            {dashboardData.students && dashboardData.students.length > 0 ? (
              <Box className="cards-grid">
                {dashboardData.students.map((student) => (
                  <Box key={student.id} className="data-card">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Box className="avatar">
                        {student.fullName.charAt(0).toUpperCase()}
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                          {student.fullName}
                        </Typography>
                        <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                          {student.company}
                        </Typography>
                      </Box>
                    </Box>
                    <Box className="data-card-content">
                      <ProgressBar value={student.progress} label="Progresso" />
                      <Box className="badge-group">
                        <Box className="badge badge-success">
                          {student.coursesCompleted} cursos
                        </Box>
                        <Box className="badge badge-warning">
                          {student.lastActivity}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box className="empty-state">
                <Typography className="empty-state-title">Nenhum estudante encontrado</Typography>
                <Typography className="empty-state-text">
                  Não há estudantes para exibir no momento.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {selectedTab === 3 && (
          <Box>
            <Box className="section-header">
              <Typography className="section-title">Performance dos Cursos</Typography>
            </Box>
            {dashboardData.courses && dashboardData.courses.length > 0 ? (
              <Box className="cards-grid">
                {dashboardData.courses.map((course) => (
                  <Box key={course.id} className="data-card">
                    <Typography className="data-card-title">{course.title}</Typography>
                    <Typography className="data-card-meta">
                      {course.enrollments} matrículas • {course.avgDuration}
                    </Typography>
                    <Box className="data-card-content">
                      <ProgressBar value={course.completionRate} label="Conclusão" />
                      <Box className="badge-group">
                        <Box className="badge badge-primary">
                          ⭐ {course.avgRating}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box className="empty-state">
                <Typography className="empty-state-title">Nenhum curso encontrado</Typography>
                <Typography className="empty-state-text">
                  Não há cursos para exibir no momento.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
